import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { Task, TaskListQuery } from '@neuroedge/shared';
import { Button, Card, PageState, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useAuth } from '../../auth/AuthContext';
import { useTasks, useCreateTask, useUpdateTask, useDeleteTask } from '../../api/tasks';
import { AssigneeSelect } from '../../components/assignee/AssigneeSelect';

interface Props {
  title?: string;
  query: TaskListQuery;
  /** Attach quick-added tasks to this contact / lead. */
  attachTo?: { contactId?: string; leadId?: string };
  showTargets?: boolean;
}

function TaskRow({
  task,
  canManage,
  showTargets,
  tenantUserId,
}: {
  task: Task;
  canManage: boolean;
  showTargets?: boolean;
  tenantUserId: string | null;
}) {
  const update = useUpdateTask();
  const del = useDeleteTask();
  const toast = useToast();
  const done = task.status === 'DONE';
  const isMine = Boolean(tenantUserId) && task.assignedUser?.tenantUserId === tenantUserId;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-2) 0',
        borderBottom: '1px solid var(--color-border)',
      }}
    >
      <input
        type="checkbox"
        checked={done}
        disabled={!canManage || update.isPending}
        onChange={(e) =>
          update.mutate(
            { id: task.id, patch: { status: e.target.checked ? 'DONE' : 'OPEN' } },
            {
              onSuccess: () =>
                toast.success(e.target.checked ? 'Task completed.' : 'Task reopened.'),
              onError: (err) =>
                toast.error(err instanceof Error ? err.message : 'Could not update the task.'),
            },
          )
        }
      />
      <span style={{ flex: 1, textDecoration: done ? 'line-through' : undefined }}>
        {task.title}
        {showTargets && task.lead ? (
          <>
            {' '}
            <Link to={`/leads/${task.lead.id}`} style={{ fontSize: 'var(--font-size-sm)' }}>
              {task.lead.title}
            </Link>
          </>
        ) : null}
        {showTargets && task.contact ? (
          <>
            {' '}
            <Link to={`/contacts/${task.contact.id}`} style={{ fontSize: 'var(--font-size-sm)' }}>
              {task.contact.displayName}
            </Link>
          </>
        ) : null}
      </span>
      {task.dueAt ? (
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
          {new Date(task.dueAt).toLocaleDateString()}
        </span>
      ) : null}
      <span className="ne-badge">{task.priority}</span>
      {canManage ? (
        <AssigneeSelect
          compact
          label={`Assigned to (${task.title})`}
          value={task.assignedUser?.tenantUserId ?? ''}
          currentName={task.assignedUser?.displayName}
          disabled={update.isPending}
          onChange={(next) =>
            update.mutate(
              { id: task.id, patch: { assignedUserId: next || null } },
              {
                onSuccess: () =>
                  toast.success(
                    !next
                      ? 'Task unassigned.'
                      : next === tenantUserId
                        ? 'Assigned to you.'
                        : 'Task assigned.',
                  ),
                onError: (err) =>
                  toast.error(err instanceof Error ? err.message : 'Could not assign the task.'),
              },
            )
          }
        />
      ) : task.assignedUser ? (
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
          {isMine ? 'Me' : task.assignedUser.displayName}
        </span>
      ) : null}
      {canManage ? (
        <Button
          variant="secondary"
          onClick={() =>
            del.mutate(task.id, {
              onSuccess: () => toast.success('Task deleted.'),
              onError: (err) =>
                toast.error(err instanceof Error ? err.message : 'Could not delete the task.'),
            })
          }
          disabled={del.isPending}
        >
          ✕
        </Button>
      ) : null}
    </div>
  );
}

/** "Add a task" form (title + assignee, defaulting to the signed-in member). */
export function TaskQuickAdd({ attachTo }: { attachTo?: Props['attachTo'] }) {
  const { tenantUserId } = useAuth();
  const create = useCreateTask();
  const toast = useToast();
  const [newTitle, setNewTitle] = useState('');
  /** Defaults to the person adding it. */
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  /** Until someone is picked, it's whoever is signed in (even if that resolves late). */
  const assignee = assigneeId ?? tenantUserId ?? '';
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    const t = newTitle.trim();
    if (t.length < 1) {
      setFieldErrors({ title: 'Enter a task title.' });
      return;
    }
    if (t.length > 200) {
      setFieldErrors({ title: 'Title must be 200 characters or fewer.' });
      return;
    }
    setFieldErrors({});
    try {
      await create.mutateAsync({
        title: t,
        assignedUserId: assignee || undefined,
        ...attachTo,
      });
      setNewTitle('');
      toast.success('Task added.');
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          toast.error('Please fix the highlighted fields.');
          return;
        }
      }
      toast.error(err instanceof Error ? err.message : 'Could not add the task.');
    }
  }

  return (
    <form onSubmit={onAdd} style={{ marginBottom: 'var(--space-3)' }} noValidate>
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <TextField
            label="Add a task"
            placeholder="Add a task…"
            value={newTitle}
            onChange={(e) => {
              setNewTitle(e.target.value);
              setFieldErrors((f) => {
                if (!f.title) return f;
                const { title: _removed, ...rest } = f;
                return rest;
              });
            }}
            error={fieldErrors.title}
          />
        </div>
        <Button type="submit" disabled={create.isPending || !newTitle.trim()}>
          Add
        </Button>
      </div>
      <AssigneeSelect value={assignee} onChange={setAssigneeId} />
    </form>
  );
}

export function TaskList({ title = 'Tasks', query, attachTo, showTargets }: Props) {
  const { hasPermission, tenantUserId } = useAuth();
  const canManage = hasPermission('tasks.manage');
  const tasks = useTasks({ pageSize: 200, sortBy: 'dueAt', sortOrder: 'asc', ...query });

  return (
    <Card title={title}>
      {canManage ? <TaskQuickAdd attachTo={attachTo} /> : null}

      {tasks.isPending ? (
        <PageState kind="loading" message="Loading tasks…" />
      ) : tasks.isError ? (
        <PageState
          kind="error"
          message={tasks.error.message}
          onRetry={() => void tasks.refetch()}
        />
      ) : tasks.data.items.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>No tasks.</p>
      ) : (
        <div>
          {tasks.data.items.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              canManage={canManage}
              showTargets={showTargets}
              tenantUserId={tenantUserId}
            />
          ))}
        </div>
      )}
    </Card>
  );
}
